// Get Started onboarding templates — per sport × persona "starting points" the
// admin can pick to instantly scaffold a real tournament/league + divisions.
// Richer sibling of `sportBootstrap.ts` (which seeds the single default on org
// creation). Supported: basketball, pickleball, volleyball, soccer.

export type OnboardingContestType = 'tournament' | 'season';

export interface OnboardingDivision {
  name: string;
  // A division.config.format value (round-robin | round-robin-playoffs |
  // pool-play | pool-play-playoffs | bracket | playoffs).
  format: string;
  // Max teams in the division (0 = unlimited).
  capacity: number;
}

export interface OnboardingTemplate {
  id: string;
  label: string;
  description: string;
  contestType: OnboardingContestType;
  // Suggested name for the created tournament/season (admin can edit).
  name: string;
  // For a tournament: sub-competition (contest) names; each gets the divisions
  // below. Omitted/empty for a season.
  contests?: string[];
  divisions: OnboardingDivision[];
}

const RR = 'round-robin';
const RR_PLAYOFFS = 'round-robin-playoffs';
const BRACKET = 'bracket';

export const ONBOARDING_TEMPLATES: Record<string, OnboardingTemplate[]> = {
  basketball: [
    {
      id: 'bb-rec-league',
      label: 'Rec league',
      description: 'An ongoing league with Upper / Lower / Open skill divisions.',
      contestType: 'season',
      name: 'Rec League',
      divisions: [
        { name: 'Upper Division', format: RR_PLAYOFFS, capacity: 8 },
        { name: 'Lower Division', format: RR_PLAYOFFS, capacity: 8 },
        { name: 'Open Division', format: RR, capacity: 8 },
      ],
    },
    {
      id: 'bb-youth-league',
      label: 'Youth league',
      description: 'A season split into age groups.',
      contestType: 'season',
      name: 'Youth League',
      divisions: [
        { name: 'U10', format: RR, capacity: 8 },
        { name: 'U12', format: RR, capacity: 8 },
        { name: 'U14', format: RR_PLAYOFFS, capacity: 8 },
      ],
    },
    {
      id: 'bb-weekend-tournament',
      label: 'Weekend tournament',
      description: "Men's and Women's contests, each a bracket.",
      contestType: 'tournament',
      name: 'Weekend Tournament',
      contests: ["Men's", "Women's"],
      divisions: [
        { name: 'Championship', format: BRACKET, capacity: 8 },
      ],
    },
  ],
  pickleball: [
    {
      id: 'pb-club-round-robin',
      label: 'Club round-robin',
      description: 'An ongoing club league with skill-rating divisions.',
      contestType: 'season',
      name: 'Club League',
      divisions: [
        { name: '3.0', format: RR, capacity: 6 },
        { name: '3.5', format: RR, capacity: 6 },
        { name: '4.0+', format: RR, capacity: 6 },
      ],
    },
    {
      id: 'pb-open-tournament',
      label: 'Open tournament',
      description: 'Mixed / Men’s / Women’s contests with skill divisions.',
      contestType: 'tournament',
      name: 'Open Tournament',
      contests: ['Mixed', "Men's", "Women's"],
      divisions: [
        { name: '3.0-3.5', format: RR_PLAYOFFS, capacity: 8 },
        { name: '3.5-4.0', format: RR_PLAYOFFS, capacity: 8 },
        { name: '4.0+', format: RR_PLAYOFFS, capacity: 8 },
      ],
    },
  ],
  volleyball: [
    {
      id: 'vb-rec-league',
      label: 'Rec league',
      description: 'An ongoing league with A / B / C skill divisions.',
      contestType: 'season',
      name: 'Rec League',
      divisions: [
        { name: 'A Division', format: RR_PLAYOFFS, capacity: 8 },
        { name: 'B Division', format: RR_PLAYOFFS, capacity: 8 },
        { name: 'C Division', format: RR, capacity: 8 },
      ],
    },
    {
      id: 'vb-weekend-tournament',
      label: 'Weekend tournament',
      description: "Men's / Women's / Coed contests, each a bracket.",
      contestType: 'tournament',
      name: 'Weekend Tournament',
      contests: ["Men's", "Women's", 'Coed'],
      divisions: [
        { name: 'Championship', format: BRACKET, capacity: 8 },
      ],
    },
  ],
  soccer: [
    {
      id: 'sc-youth-club-league',
      label: 'Youth club league',
      description: 'A season split into age groups (U8–U14).',
      contestType: 'season',
      name: 'Youth League',
      divisions: [
        { name: 'U8', format: RR, capacity: 8 },
        { name: 'U10', format: RR, capacity: 8 },
        { name: 'U12', format: RR, capacity: 8 },
        { name: 'U14', format: RR_PLAYOFFS, capacity: 8 },
      ],
    },
    {
      id: 'sc-youth-tournament',
      label: 'Youth tournament',
      description: 'Boys and Girls contests with age-group brackets.',
      contestType: 'tournament',
      name: 'Youth Cup',
      contests: ['Boys', 'Girls'],
      divisions: [
        { name: 'U10', format: BRACKET, capacity: 8 },
        { name: 'U12', format: BRACKET, capacity: 8 },
        { name: 'U14', format: BRACKET, capacity: 8 },
      ],
    },
    {
      id: 'sc-adult-league',
      label: 'Adult league',
      description: 'An ongoing adult league with two divisions.',
      contestType: 'season',
      name: 'Adult League',
      divisions: [
        { name: 'Division 1', format: RR_PLAYOFFS, capacity: 8 },
        { name: 'Division 2', format: RR_PLAYOFFS, capacity: 8 },
      ],
    },
  ],
};

export const ONBOARDING_SUPPORTED_SPORTS = ['basketball', 'pickleball', 'volleyball', 'soccer'] as const;

export function getOnboardingTemplates(sport: string | null | undefined): OnboardingTemplate[] {
  const key = (sport ?? '').toLowerCase();
  return ONBOARDING_TEMPLATES[key] ?? ONBOARDING_TEMPLATES.basketball;
}

// Valid division formats the AI/free-text path is constrained to.
export const ONBOARDING_FORMATS = [
  'round-robin',
  'round-robin-playoffs',
  'pool-play',
  'pool-play-playoffs',
  'bracket',
  'playoffs',
] as const;
